/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import i18n from '@/i18n'
import { API_BASE, apiFetch } from '@/lib/api'
import { purgeAllHelpSearches } from '@/lib/helpSearchHistory'
import { lireLeRefusDeConnexion, REFUS_SANS_REPONSE } from '@/contexts/refusDeConnexion'
import { fusionnerLesPreferences, type DeepPartial, type UserPreferences } from '@/contexts/preferencesUtilisateur'
import type { AuthContextType, EntityPermissions, LoginResult, User } from '@/contexts/authContext.types'

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
    children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null)
    const [entities, setEntities] = useState<EntityPermissions[]>([])
    const [isAdmin, setIsAdmin] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [shouldPromptMfa, setShouldPromptMfa] = useState(false)

    const isAuthenticated = user !== null

    // ⚠️ `/espace` couvre TOUT le préfixe (`startsWith` ci-dessous) : les pages
    // d'accès d'un espace externe sont publiques par nature — la personne y
    // vient justement créer ou réparer son mot de passe. Sans cette entrée,
    // `checkAuth` appelle `/auth/me`, reçoit 401, et redirige vers `/login`
    // avant même que la page ne s'affiche (EPIC 49, PORT-006 §4). Idem pour
    // `/account/confirm-email-change` : le lien s'ouvre souvent sans session (PREF-002).
    const PUBLIC_PATHS = ['/login', '/signup', '/verify-email', '/forgot-password', '/reset-password', '/set-password', '/account/confirm-email-change', '/mfa-setup', '/mfa-verify', '/knowledge', '/developers', '/sign', '/action', '/quote', '/inscription', '/espace']

    const isPublicPath = useCallback(() => {
        const path = window.location.pathname
        return PUBLIC_PATHS.some(p => path === p || path.startsWith(`${p}/`))
    // Intentional: PUBLIC_PATHS is a static array defined inside the component, no need to track it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const checkAuth = useCallback(async () => {
        if (isPublicPath()) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)
            const response = await apiFetch(`${API_BASE}/auth/me`)

            if (response.ok) {
                const data = await response.json()
                setUser({
                    id: data.user.id,
                    email: data.user.email,
                    firstName: data.user.first_name,
                    lastName: data.user.last_name,
                    fullName: data.user.full_name,
                    avatarUrl: data.user.avatar_url || null,
                    preferences: data.user.preferences
                })
                setEntities(data.entities)
                setIsAdmin(data.is_admin ?? false)
                setShouldPromptMfa(data.should_prompt_mfa ?? false)
                Sentry.setUser({ id: data.user.id })
                Sentry.setTag('tenant', window.location.hostname.split('.')[0])
                const userLang = data.user.preferences?.global?.language
                if (userLang && userLang !== i18n.language?.slice(0, 2)) {
                    i18n.changeLanguage(userLang)
                }
            } else {
                // PORT-002 §5 / PORT-008 §4 — un compte d'ESPACE qui tape `/` ou
                // `/dashboard` reçoit `403 PORTAL_ACCOUNT_ONLY`. On le conduit à
                // son espace, sans modale ni message : il n'a rien fait de mal,
                // il s'est trompé de porte. Le laisser sur `/login` lui montrerait
                // une page aux couleurs d'Argalis qui refusera son compte par
                // construction — l'appel au support est garanti.
                if (response.status === 403) {
                    try {
                        const corps = await response.clone().json()
                        if (corps?.detail?.code === 'PORTAL_ACCOUNT_ONLY') {
                            window.location.replace('/espace')
                            return
                        }
                    } catch { /* corps illisible : on retombe sur le cas commun */ }
                }
                setUser(null)
                setEntities([])
                setIsAdmin(false)
                setShouldPromptMfa(false)
            }
        } catch {
            setUser(null)
            setEntities([])
            setIsAdmin(false)
        } finally {
            setIsLoading(false)
        }
    // Intentional: checkAuth should only depend on isPublicPath; adding state setters would cause infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        checkAuth()
    }, [checkAuth])

    const updatePreferences = useCallback(async (patch: DeepPartial<UserPreferences>) => {
        if (user) {
            const newPrefs = fusionnerLesPreferences(user.preferences, patch)
            setUser({ ...user, preferences: newPrefs })
            if (patch.global?.language && patch.global.language !== i18n.language?.slice(0, 2)) {
                i18n.changeLanguage(patch.global.language)
            }
        }

        try {
            await apiFetch(`${API_BASE}/auth/preferences`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patch),
            })
        } catch (e) {
            console.error("Failed to save preferences", e)
        }
    }, [user])

    const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
        try {
            setError(null)
            setIsLoading(true)

            const response = await apiFetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            })

            if (response.ok) {
                const data = await response.json()

                if (data.mfa_required) {
                    return {
                        success: false,
                        mfa_required: true,
                        mfa_token: data.mfa_token,
                    }
                }

                const loginUser: User = {
                    id: data.user.id,
                    email: data.user.email,
                    firstName: data.user.first_name,
                    lastName: data.user.last_name,
                    fullName: data.user.full_name,
                    avatarUrl: data.user.avatar_url || null,
                    preferences: data.user.preferences,
                }
                setUser(loginUser)

                if (data.entities) {
                    setEntities(data.entities)
                    setIsAdmin(data.is_admin ?? false)
                    setShouldPromptMfa(data.should_prompt_mfa ?? false)
                    Sentry.setUser({ id: data.user.id })
                    Sentry.setTag('tenant', window.location.hostname.split('.')[0])
                    const userLang = data.user.preferences?.global?.language
                    if (userLang && userLang !== i18n.language?.slice(0, 2)) {
                        i18n.changeLanguage(userLang)
                    }
                } else {
                    await checkAuth()
                }

                if (data.mfa_skippable) {
                    return { success: true, mfa_skippable: true }
                }
                return { success: true }
            } else {
                // SEC — le refus se lit par la règle UNIQUE des trois portes de
                // connexion (`refusDeConnexion`). Cette porte-ci, celle du
                // sous-domaine, disait « mot de passe incorrect » à un 503
                // SERVICE_BUSY, à un 500 et à un 429 — au BON mot de passe.
                // 🔴 A33 — `EMAIL_NOT_VERIFIED` : `messageKey` NUL, donc `error`
                // RESTE nul (vidé en tête de `login`) — le mot de passe était
                // JUSTE, aucun lecteur de `useAuth().error` n'y lit « incorrect ».
                const refus = await lireLeRefusDeConnexion(response)
                setError(refus.messageKey)
                return refus.adresseNonVerifiee
                    ? { success: false, email_not_verified: true }
                    : { success: false }
            }
        } catch {
            setError(REFUS_SANS_REPONSE.messageKey) // réseau coupé, 500 sans CORS : la phrase d'un 5xx
            return { success: false }
        } finally {
            setIsLoading(false)
        }
    }, [checkAuth])

    const logout = useCallback(async () => {
        try {
            await apiFetch(`${API_BASE}/auth/logout`, {
                method: 'POST',
            })
        } finally {
            setUser(null)
            setEntities([])
            setIsAdmin(false)
            Sentry.setUser(null)
            // RGPD (HELP-DRAWER §11) : l'historique de recherche d'aide (texte libre
            // potentiellement sensible) ne survit pas à la déconnexion.
            purgeAllHelpSearches()
        }
    }, [])

    const updateUser = useCallback((patch: Partial<User>) => {
        setUser(prev => prev ? { ...prev, ...patch } : prev)
    }, [])

    const hasPermission = useCallback((slug: string): boolean => {
        if (isAdmin) return true
        return entities.some(e => e.permissions.includes(slug))
    }, [entities, isAdmin])

    /**
     * Ce compte est-il RESTREINT par ce droit ? — jumeau front de
     * `api/_owner_scope`, et le seul endroit qui porte l'exemption.
     *
     * 🔴 **Un slug de restriction ne se lit jamais avec `hasPermission`.** Celui
     * du dessus renvoie `true` pour TOUT quand `isAdmin` : demandé sur
     * `quotes:stats_own_only`, il répond « oui » à un super administrateur qui ne
     * porte aucune restriction, et l'écran se borne tout seul. Le serveur, lui,
     * exempte explicitement le super admin (`apply_stats_own_only` →
     * `_is_super_admin`) et sert le périmètre entier : les deux côtés du fil
     * disaient le contraire l'un de l'autre.
     *
     * Second chemin vers le même défaut, indépendant du joker : le seed accorde
     * au rôle Super Admin la TOTALITÉ des permissions, restrictions comprises,
     * sauf celles rangées dans `permissions_exclude`
     * (`backend/src/cli/_auth_seed_roles.py`) — d'où la lecture par entité, elle
     * aussi neutralisée ici.
     *
     * Une restriction ne s'HÉRITE pas et ne se CUMULE pas : elle vaut pour qui la
     * porte vraiment, et pour personne d'autre.
     */
    const hasRestriction = useCallback((slug: string): boolean => {
        if (isAdmin) return false
        return entities.some(e => e.permissions.includes(slug))
    }, [entities, isAdmin])

    const value = useMemo<AuthContextType>(() => ({
        user,
        entities,
        isAuthenticated,
        isAdmin,
        isLoading,
        error,
        shouldPromptMfa,
        hasPermission,
        hasRestriction,
        login,
        logout,
        checkAuth,
        updatePreferences,
        updateUser,
    }), [user, entities, isAuthenticated, isAdmin, isLoading, error,
        shouldPromptMfa, hasPermission, hasRestriction, login, logout, checkAuth,
        updatePreferences, updateUser])

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
